import { gql } from '@apollo/client/core';
import {
  TRANSCRIPT_TYPE_FIELDS,
  TRANSCRIPT_REQUEST_FIELDS,
  TRANSCRIPT_ACTIVITY_FIELDS,
  GENERATED_TRANSCRIPT_FIELDS,
  TRANSCRIPT_TEMPLATE_FIELDS,
} from './fragments';

export const GET_TRANSCRIPT_TYPES = gql`
  query GetTranscriptTypes {
    transcriptTypes {
      ...TranscriptTypeFields
    }
  }
  ${TRANSCRIPT_TYPE_FIELDS}
`;

export const GET_TRANSCRIPT_REQUESTS = gql`
  query GetTranscriptRequests(
    $status: TranscriptRequestStatusEnum
    $transcript_type_id: ID
    $applicant_category: ApplicantCategoryEnum
    $search: String
    $date_from: Date
    $date_to: Date
    $first: Int = 15
    $page: Int = 1
  ) {
    transcriptRequests(
      filter: {
        status: $status
        transcript_type_id: $transcript_type_id
        applicant_category: $applicant_category
        search: $search
        date_from: $date_from
        date_to: $date_to
      }
      first: $first
      page: $page
    ) {
      data {
        ...TranscriptRequestFields
      }
      paginatorInfo {
        currentPage
        perPage
        total
        lastPage
        hasMorePages
      }
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
`;

export const GET_TRANSCRIPT_REQUEST = gql`
  query GetTranscriptRequest($id: ID!) {
    transcriptRequest(id: $id) {
      ...TranscriptRequestFields
      generatedTranscript {
        ...GeneratedTranscriptFields
      }
      activities {
        ...TranscriptActivityFields
      }
      bill {
        id
        status
        amount
        net_amount
        amount_paid
        balance
        due_date
      }
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
  ${GENERATED_TRANSCRIPT_FIELDS}
  ${TRANSCRIPT_ACTIVITY_FIELDS}
`;

export const GET_GENERATED_TRANSCRIPT = gql`
  query GetGeneratedTranscript($id: ID!) {
    generatedTranscript(id: $id) {
      ...GeneratedTranscriptFields
      student {
        id
        first_name
        surname
        matric_no
      }
      transcriptRequest {
        id
        tracking_code
        status
      }
    }
  }
  ${GENERATED_TRANSCRIPT_FIELDS}
`;

export const GET_PUBLIC_TRANSCRIPT_TYPES = gql`
  query GetPublicTranscriptTypes($school_slug: String!) {
    publicTranscriptTypes(school_slug: $school_slug) {
      id
      name
      slug
      description
      category
      is_active
      billItemTemplate {
        id
        name
        default_amount
      }
    }
  }
`;

export const GET_TRANSCRIPT_REQUEST_STATUS = gql`
  query GetTranscriptRequestStatus($tracking_code: String!) {
    transcriptRequestStatus(tracking_code: $tracking_code) {
      tracking_code
      status
      applicant_name
      transcript_type
      submitted_at
      approved_at
      rejection_reason
      can_download
      activities {
        id
        type
        content
        old_status
        new_status
        is_visible_to_applicant
        created_at
      }
    }
  }
`;

export const RETRIEVE_MATRIC_NO = gql`
  query RetrieveMatricNo($input: RetrieveMatricNoInput!, $school_slug: String!) {
    retrieveMatricNo(input: $input, school_slug: $school_slug) {
      found
      student_id
      matric_no
      message
    }
  }
`;

export const GET_PUBLIC_PROGRAMMES = gql`
  query GetPublicProgrammes($school_slug: String!) {
    publicProgrammes(school_slug: $school_slug) {
      id
      program_name
      prog_code
    }
  }
`;

export const GET_TRANSCRIPT_TEMPLATES = gql`
  query GetTranscriptTemplates {
    transcriptTemplates {
      ...TranscriptTemplateFields
    }
  }
  ${TRANSCRIPT_TEMPLATE_FIELDS}
`;

export const GET_TRANSCRIPT_TEMPLATE = gql`
  query GetTranscriptTemplate($id: ID!) {
    transcriptTemplate(id: $id) {
      ...TranscriptTemplateFields
    }
  }
  ${TRANSCRIPT_TEMPLATE_FIELDS}
`;

export const GET_TRANSCRIPT_DESIGN_OPTIONS = gql`
  query GetTranscriptDesignOptions {
    transcriptDesignOptions {
      value
      label
      description
    }
  }
`;

export const GET_TRANSCRIPT_PRESET_OPTIONS = gql`
  query GetTranscriptPresetOptions {
    transcriptPresetOptions {
      key
      name
      description
      config
    }
  }
`;

export const GET_PDF_TRANSCRIPT_CONFIG = gql`
  query GetPdfTranscriptConfig {
    pdfTranscriptConfig {
      preset
      logo_position
      logo_height
      show_school_name
      show_school_address
      show_report_title
      report_title
      report_subtitle
      font_family
      font_size
      header_font_size
      table_header_font_size
      row_padding
      show_borders
      striped_rows
      alternate_row_color
      show_student_info
      student_info_fields
      student_info_layout
      show_cover_letter
      show_cumulative
      show_semester_summary
      show_partial_footnote
      show_footer
      footer_text
      show_estamp
      show_signature_lines
      signature_labels
      page_size
      page_orientation
      page_margin
      show_watermark
      watermark_text
    }
  }
`;

export const GET_PDF_TRANSCRIPT_PRESETS = gql`
  query GetPdfTranscriptPresets {
    pdfTranscriptPresets {
      key
      name
      description
      config
    }
  }
`;

export const GET_TRANSCRIPT_STUDENT_INFO_FIELDS = gql`
  query GetTranscriptStudentInfoFields {
    transcriptStudentInfoFields {
      key
      label
    }
  }
`;

export const GET_TRANSCRIPT_FONT_FAMILIES = gql`
  query GetTranscriptFontFamilies {
    transcriptFontFamilies {
      key
      label
    }
  }
`;

export const DOWNLOAD_TRANSCRIPT_PDF = gql`
  query DownloadTranscriptPdf($generated_transcript_id: ID!, $category: TranscriptTypeCategoryEnum) {
    downloadTranscriptPdf(generated_transcript_id: $generated_transcript_id, category: $category) {
      id
      status
      progress
      file_name
      file_url
      error_message
    }
  }
`;

export const DOWNLOAD_PUBLIC_TRANSCRIPT_PDF = gql`
  query DownloadPublicTranscriptPdf($tracking_code: String!, $applicant_email: String!) {
    downloadPublicTranscriptPdf(tracking_code: $tracking_code, applicant_email: $applicant_email) {
      id
      status
      progress
      file_name
      file_url
      error_message
    }
  }
`;

export const GET_MY_TRANSCRIPT_REQUESTS = gql`
  query GetMyTranscriptRequests {
    myTranscriptRequests {
      ...TranscriptRequestFields
      generatedTranscript {
        id
        version
        generated_at
        file_path
      }
      activities {
        ...TranscriptActivityFields
      }
      bill {
        id
        status
        amount
        net_amount
        amount_paid
        balance
        due_date
      }
    }
  }
  ${TRANSCRIPT_REQUEST_FIELDS}
  ${TRANSCRIPT_ACTIVITY_FIELDS}
`;

export const GET_MATRIC_RETRIEVAL_ATTEMPTS = gql`
  query GetMatricRetrievalAttempts($page: Int, $first: Int, $status: MatricRetrievalStatusEnum, $result: MatricRetrievalResultEnum) {
    matricRetrievalAttempts(page: $page, first: $first, status: $status, result: $result) {
      data {
        id
        surname
        first_name
        other_name
        email
        phone
        programme {
          id
          program_name
        }
        year_of_graduation
        result
        status
        matchedStudent {
          id
          matric_no
          first_name
          surname
        }
        resolver {
          id
          name
        }
        resolved_at
        admin_notes
        created_at
      }
      paginatorInfo {
        currentPage
        lastPage
        total
      }
    }
  }
`;
